import { router } from '@inertiajs/react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { SearchInput } from '@/components/shared/SearchInput'
import { Pagination, PaginationMeta } from '@/components/shared/Pagination'
import { Badge } from '@/components/ui/badge'
import { UserCircle } from 'lucide-react'
import { formatDate } from '@/lib/utils'

type BackofficeUser = {
  id: string
  name: string
  email_address: string
  role: 'member' | 'admin' | 'owner'
  account_id: string
  account_name: string
  account_slug: string
  removed: boolean
  created_at: string
}

type Props = {
  users: BackofficeUser[]
  search: string
  pagination: PaginationMeta
}

function RoleBadge({ role }: { role: string }) {
  const config: Record<string, string> = {
    owner: 'bg-purple-100 text-purple-700 border-purple-200',
    admin: 'bg-blue-100 text-blue-700 border-blue-200',
    member: 'bg-secondary text-secondary-foreground',
  }
  return (
    <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium border ${config[role] ?? config.member}`}>
      {role}
    </span>
  )
}

export default function BackofficeUsersIndex({ users, search, pagination }: Props) {
  return (
    <BackofficeLayout
      title="Users"
      breadcrumbs={[
        { label: 'Backoffice', href: '/backoffice/dashboard' },
        { label: 'Users' },
      ]}
    >
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Users</h1>
          <p className="text-sm text-muted-foreground">{pagination.total} total</p>
        </div>
      </div>

      <SearchInput value={search} placeholder="Search by name or email..." />

      <div className="mt-4">
        {users.length === 0 ? (
          <div className="border rounded-lg py-16 text-center">
            <UserCircle className="w-6 h-6 mx-auto mb-3 text-muted-foreground/30" />
            <h3 className="text-sm font-medium text-foreground mb-1">No users found</h3>
            <p className="text-sm text-muted-foreground">Users appear here after they register.</p>
          </div>
        ) : (
          <div className="border rounded-lg">
            {users.map((u, i) => (
              <div
                key={u.id}
                className={`flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors ${i > 0 ? 'border-t' : ''} ${u.removed ? 'opacity-50' : ''}`}
                onClick={() => router.visit(`/backoffice/users/${u.id}`)}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <span className="text-xs font-medium text-muted-foreground">
                      {u.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground truncate">{u.name}</span>
                      <RoleBadge role={u.role} />
                      {u.removed && (
                        <Badge variant="outline" className="text-[10px] text-red-600 border-red-200">Removed</Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">{u.email_address}</div>
                    <div className="text-xs text-muted-foreground font-mono">{u.account_name} · /{u.account_slug}</div>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground shrink-0 ml-4">
                  {formatDate(u.created_at)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4">
        <Pagination pagination={pagination} />
      </div>
    </BackofficeLayout>
  )
}
